import * as Dialog from "@radix-ui/react-dialog";
import { filterClasses } from "@swivl-mono/helpers";
import { SearchInput } from "@swivl-mono/ui";
import { KeyboardEvent, ReactNode } from "react";

import { SEARCH_CATEGORY_FILTERS } from "./GlobalSearch.Categories";
import { SEARCH_EXAMPLES } from "./GlobalSearch.Examples";
import { SearchEntry } from "./GlobalSearch.Types";
import GlobalSearchModalModel from "./GlobalSearchModal.Model";
import { useGlobalSearch } from "./useGlobalSearch";

/** Wraps the first case-insensitive match of `query` in `text` with a <mark>. */
function highlightMatch(text: string, query: string): ReactNode {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="rounded-[2px] bg-orange-50 text-orange-600">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export function GlobalSearchModal(): React.ReactElement {
  const { query, setQuery, hasSearched, submit, submitQuery, clear, groupedResults, resultCount, selectResult, activeCategory, setActiveCategory } =
    useGlobalSearch();

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") submit();
    else if (event.key === "Escape") GlobalSearchModalModel.close();
  };

  const handleSelect = (entry: SearchEntry): void => {
    selectResult(entry);
    GlobalSearchModalModel.close();
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && GlobalSearchModalModel.close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-500 bg-black/40" />
        <Dialog.Content className="fixed top-24 left-1/2 z-500 w-[640px] max-w-[85vw] -translate-x-1/2 rounded-lg bg-white shadow-[0_4px_8px_rgba(0,0,0,0.15),0_30px_60px_-20px_rgba(0,0,0,0.35)]">
          <Dialog.Title className="sr-only">Search Property Manager</Dialog.Title>
          <Dialog.Description className="sr-only">Search locations, Details, Policies, and Reports.</Dialog.Description>

          <div className="p-3">
            <div className="relative">
              <SearchInput
                autoFocus
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onClear={clear}
              />
              {!hasSearched && (
                <span className="pointer-events-none absolute top-1/2 right-9 -translate-y-1/2 rounded border border-grey-300 bg-grey-50 px-1.5 py-0.5 font-mono text-[10px] text-grey-500">
                  ↵ to search
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2 px-3 py-2">
            {SEARCH_CATEGORY_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setActiveCategory(filter.id)}
                className={filterClasses(
                  "cursor-pointer rounded-full px-3 py-1 text-sm font-semibold transition-colors",
                  activeCategory === filter.id ? "bg-orange-50 text-orange-600" : "border border-grey-300 text-grey-600 hover:bg-grey-50"
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {!hasSearched ? (
              <div className="p-3">
                <div className="px-1 pb-1 font-mono text-[11px] font-bold tracking-wide text-grey-700 uppercase">Examples</div>
                {SEARCH_EXAMPLES[activeCategory].map((example) => (
                  <button
                    key={example}
                    type="button"
                    onClick={() => submitQuery(example)}
                    className="block w-full cursor-pointer rounded-sm px-2 py-2 text-left text-sm text-grey-900 hover:bg-grey-50"
                  >
                    {example}
                  </button>
                ))}
              </div>
            ) : resultCount === 0 ? (
              <p className="body-sm p-4 text-grey-500">No results for &quot;{query}&quot;.</p>
            ) : (
              groupedResults.map((group) => (
                <div key={group.category}>
                  <div className="px-3 pt-3 pb-1 font-mono text-[11px] font-bold tracking-wide text-grey-700 uppercase">{group.category}</div>
                  {group.matches.map(({ entry, titleMatch }) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => handleSelect(entry)}
                      className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-grey-50"
                    >
                      <span className="body-sm-alt line-clamp-2 break-words text-grey-900">
                        {titleMatch ? highlightMatch(entry.title, query) : entry.title}
                      </span>
                      {entry.breadcrumb && <span className="line-clamp-1 text-xs break-words text-grey-500">{entry.breadcrumb}</span>}
                      {!titleMatch && entry.snippet && (
                        <span className="line-clamp-2 text-xs break-words text-grey-600">{highlightMatch(entry.snippet, query)}</span>
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
